import { type ClassValue, clsx } from 'clsx'
import { twMerge } from 'tailwind-merge'

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

// Format numbers for display
export function formatNumber(
  value: number,
  options?: {
    decimals?: number
    compact?: boolean
    currency?: string
  },
): string {
  const { decimals = 2, compact = false, currency } = options || {}

  if (compact) {
    return new Intl.NumberFormat('en-US', {
      notation: 'compact',
      maximumFractionDigits: decimals,
      style: currency ? 'currency' : 'decimal',
      currency: currency,
    }).format(value)
  }

  return new Intl.NumberFormat('en-US', {
    maximumFractionDigits: decimals,
    minimumFractionDigits: 0,
    style: currency ? 'currency' : 'decimal',
    currency: currency,
  }).format(value)
}

// Format dates for display
export function formatDate(
  date: string | Date,
  options?: {
    style?: 'short' | 'medium' | 'long' | 'relative'
    includeTime?: boolean
  },
): string {
  const { style = 'medium', includeTime = false } = options || {}
  const dateObj = typeof date === 'string' ? new Date(date) : date

  if (style === 'relative') {
    return formatRelativeDate(dateObj)
  }

  const formatOptions: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: style === 'short' ? 'short' : 'long',
    day: 'numeric',
  }

  if (includeTime) {
    formatOptions.hour = '2-digit'
    formatOptions.minute = '2-digit'
  }

  return new Intl.DateTimeFormat('en-US', formatOptions).format(dateObj)
}

// Format relative dates (e.g., "2 hours ago")
export function formatRelativeDate(date: Date): string {
  const now = new Date()
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000)

  const intervals = [
    { label: 'year', seconds: 31536000 },
    { label: 'month', seconds: 2592000 },
    { label: 'day', seconds: 86400 },
    { label: 'hour', seconds: 3600 },
    { label: 'minute', seconds: 60 },
    { label: 'second', seconds: 1 },
  ]

  for (const interval of intervals) {
    const count = Math.floor(diffInSeconds / interval.seconds)
    if (count >= 1) {
      return `${count} ${interval.label}${count > 1 ? 's' : ''} ago`
    }
  }

  return 'just now'
}

// Copy text to clipboard
export async function copyToClipboard(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text)
    return true
  } catch (error) {
    console.error('Failed to copy to clipboard:', error)
    return false
  }
}

// Download file from blob
export function downloadFile(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}

// Serialize a payload to a JSON file and trigger a browser download.
// Caller is responsible for filename collision/extension.
export function downloadJSON(payload: unknown, filename: string): void {
  const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' })
  downloadFile(blob, filename)
}

// Validate email format
export function isValidEmail(email: string): boolean {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  return emailRegex.test(email)
}

// Generate random ID
export function generateId(): string {
  return Math.random().toString(36).substring(2, 15)
}

// Debounce function
export function debounce<T extends (...args: never[]) => unknown>(
  func: T,
  wait: number,
): (...args: Parameters<T>) => void {
  let timeout: ReturnType<typeof setTimeout>
  return function executedFunction(...args: Parameters<T>) {
    const later = () => {
      clearTimeout(timeout)
      func(...args)
    }
    clearTimeout(timeout)
    timeout = setTimeout(later, wait)
  }
}

// Throttle function
export function throttle<T extends (...args: never[]) => unknown>(
  func: T,
  limit: number,
): (...args: Parameters<T>) => void {
  let inThrottle: boolean
  return function executedFunction(this: unknown, ...args: Parameters<T>) {
    if (!inThrottle) {
      func.apply(this, args)
      inThrottle = true
      setTimeout(() => (inThrottle = false), limit)
    }
  }
}

// Get environment config
export function getEnvConfig() {
  return {
    API_BASE_URL: import.meta.env.VITE_API_BASE_URL || '/api/v1',
    APP_NAME: import.meta.env.VITE_APP_NAME || 'EcoSemantic',
    APP_VERSION: import.meta.env.VITE_APP_VERSION || '1.0.0',
    MCP_ENDPOINT: import.meta.env.VITE_MCP_ENDPOINT || 'https://mcp.ecosemantic.com/mcp',
    DEV_MODE: import.meta.env.VITE_DEV_MODE === 'true',
    DEBUG: import.meta.env.VITE_DEBUG === 'true',
  }
}

// Check if user is authenticated
export function isAuthenticated(): boolean {
  return !!localStorage.getItem('auth_token')
}

// Get auth token
export function getAuthToken(): string | null {
  return localStorage.getItem('auth_token')
}

// Remove auth token
export function removeAuthToken(): void {
  localStorage.removeItem('auth_token')
}

// Format LCA result for display
export function formatLCAResult(
  value: number,
  unit: string,
  options?: { decimals?: number },
): string {
  const { decimals = 3 } = options || {}
  const formattedValue = formatNumber(value, { decimals })
  return `${formattedValue} ${unit}`
}

// Parse method specification
export function parseMethodSpecification(method: string): {
  database: string
  family: string
  category: string
  indicator: string
} | null {
  const parts = method.split(' > ')
  if (parts.length !== 4) return null

  return {
    database: parts[0].trim(),
    family: parts[1].trim(),
    category: parts[2].trim(),
    indicator: parts[3].trim(),
  }
}

// Format tier name for display
export function formatTierName(tier: string): string {
  return tier.charAt(0).toUpperCase() + tier.slice(1)
}

// Calculate usage percentage
export function calculateUsagePercentage(used: number, limit: number): number {
  if (limit === 0) return 0
  return Math.min((used / limit) * 100, 100)
}

// Get usage status color
export function getUsageStatusColor(percentage: number): string {
  if (percentage >= 90) return 'text-red-500'
  if (percentage >= 75) return 'text-yellow-500'
  return 'text-green-500'
}

// Sleep utility
export function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

// Retry with exponential backoff
export async function retryWithBackoff<T>(
  fn: () => Promise<T>,
  maxRetries: number = 3,
  baseDelay: number = 1000,
): Promise<T> {
  let lastError: Error

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await fn()
    } catch (error) {
      lastError = error as Error

      if (attempt === maxRetries) {
        throw lastError
      }

      const delay = baseDelay * Math.pow(2, attempt)
      await sleep(delay)
    }
  }

  throw lastError!
}
